'use client';

import React, { Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { StoreFooter } from '../../../components/StoreChrome';
import SiteHeader from '../../../components/SiteHeader';
import { useCart } from '../../../components/CartProvider';
import Logo from '../../../components/Logo';
import { IconCoracao, IconComentario, IconCompartilhar, IconSalvar } from '../../../components/SocialIcons';
import {
  Categoria,
  Produto,
  listarCategorias,
  listarProdutos,
  money,
  moneyMensal,
} from '../../../lib/store-api';

type Layout = 'post' | 'grid3' | 'lista';
const LAYOUT_KEY = 'catalogo-layout';

function useAdicionar(produto: Produto) {
  const { addItem } = useCart();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  async function handleAdd(e: React.SyntheticEvent) {
    e.preventDefault();
    if (!produto.disponivel || adding) return;
    setAdding(true);
    try {
      await addItem(produto.id, 1);
      setAdded(true);
      setTimeout(() => setAdded(false), 1500);
    } catch {
      // O carrinho fica inalterado; o botao simplesmente volta ao estado normal.
    } finally {
      setAdding(false);
    }
  }

  return { adding, added, handleAdd };
}

function BadgeDestaque() {
  const tp = useTranslations('produtos');
  return (
    <span
      className="absolute top-2 left-2 z-10 text-white text-xs px-2 py-0.5 font-medium"
      style={{ background: '#000', borderRadius: '2px', fontSize: '11px' }}
    >
      {tp('destaque')}
    </span>
  );
}

function BadgeIndisponivel() {
  const tp = useTranslations('produtos');
  return (
    <span
      className="absolute top-2 right-2 z-10 text-xs px-2 py-0.5 font-medium"
      style={{
        background: '#fff',
        border: '1px solid #ccc',
        borderRadius: '2px',
        fontSize: '11px',
      }}
    >
      {tp('indisponivel')}
    </span>
  );
}

/** Card estilo post de feed (Instagram): header de "perfil", mídia, ações e legenda. */
function ProdutoCardPost({ produto }: { produto: Produto }) {
  const t = useTranslations('paginaCatalogo');
  const tp = useTranslations('produtos');
  const { adding, added, handleAdd } = useAdicionar(produto);
  const [curtido, setCurtido] = useState(false);
  const [salvo, setSalvo] = useState(false);

  return (
    <article
      className="bg-white"
      style={{ border: '1px solid #dbdbdb', borderRadius: '8px', overflow: 'hidden' }}
    >
      <div className="flex items-center gap-3" style={{ padding: '11px 14px' }}>
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: '34px',
            height: '34px',
            borderRadius: '999px',
            background: '#eeeeee',
            padding: '5px',
          }}
        >
          <Logo style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">enq.digital</p>
          {produto.categoria && (
            <p className="text-gray-500 leading-tight" style={{ fontSize: '12px' }}>
              {produto.categoria}
            </p>
          )}
        </div>
      </div>

      <a
        href={`/produto/${produto.slug}`}
        className="relative block bg-gray-100"
        style={{ aspectRatio: '1/1' }}
      >
        {produto.destaque && <BadgeDestaque />}
        {!produto.disponivel && <BadgeIndisponivel />}
        {produto.imagem ? (
          <img src={produto.imagem} alt={produto.nome} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
            {tp('semImagem')}
          </div>
        )}
      </a>

      <div style={{ padding: '10px 14px 4px' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              aria-label={curtido ? t('descurtir') : t('curtir')}
              onClick={() => setCurtido((v) => !v)}
              style={{ color: curtido ? '#ed4956' : '#000' }}
            >
              <IconCoracao />
            </button>
            <a
              href={`/produto/${produto.slug}`}
              aria-label={t('verDetalhes')}
              style={{ color: '#000' }}
            >
              <IconComentario />
            </a>
            <button aria-label={t('compartilhar')} style={{ color: '#000' }}>
              <IconCompartilhar />
            </button>
          </div>
          <button
            aria-label={salvo ? t('removerDosSalvos') : t('salvar')}
            onClick={() => setSalvo((v) => !v)}
            style={{ color: '#000' }}
          >
            <IconSalvar filled={salvo} />
          </button>
        </div>
      </div>

      <div style={{ padding: '6px 14px 14px' }}>
        <p className="text-sm">
          <span className="font-semibold">enq.digital</span>{' '}
          <span className="font-semibold">{produto.nome}.</span>{' '}
          <span className="text-gray-700">{produto.descricaoCurta}</span>
        </p>
        <div className="flex items-center justify-between gap-3 mt-3">
          <div>
            {produto.precoDe && (
              <p className="text-gray-400 text-xs" style={{ textDecoration: 'line-through' }}>
                {money(produto.precoDe)}
              </p>
            )}
            <p className="font-semibold text-sm">{moneyMensal(produto.preco)}</p>
          </div>
          <button
            onClick={handleAdd}
            disabled={!produto.disponivel || adding}
            className="btn-outline"
            style={{
              padding: '7px 16px',
              fontSize: '13px',
              opacity: !produto.disponivel ? 0.4 : 1,
            }}
          >
            {added ? tp('adicionado') : tp('adicionar')}
          </button>
        </div>
      </div>
    </article>
  );
}

/** Grade tradicional de e-commerce: 2 colunas no mobile, 3 no desktop. */
function ProdutoCardGrid({ produto }: { produto: Produto }) {
  const tp = useTranslations('produtos');
  const { adding, added, handleAdd } = useAdicionar(produto);

  return (
    <a href={`/produto/${produto.slug}`} className="product-card flex flex-col">
      <div
        className="relative overflow-hidden bg-gray-100"
        style={{ aspectRatio: '1/1', borderRadius: '4px' }}
      >
        {produto.destaque && <BadgeDestaque />}
        {!produto.disponivel && <BadgeIndisponivel />}
        {produto.imagem ? (
          <img src={produto.imagem} alt={produto.nome} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
            {tp('semImagem')}
          </div>
        )}
      </div>
      <div className="pt-3 flex-1 flex flex-col">
        {produto.categoria && (
          <p
            className="text-gray-500"
            style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            {produto.categoria}
          </p>
        )}
        <p className="text-sm text-black leading-snug mb-1" style={{ fontSize: '14px' }}>
          {produto.nome}
        </p>
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <div>
            {produto.precoDe && (
              <p className="text-gray-400 text-xs" style={{ textDecoration: 'line-through' }}>
                {money(produto.precoDe)}
              </p>
            )}
            <p className="font-semibold text-sm" style={{ fontSize: '14px' }}>
              {moneyMensal(produto.preco)}
            </p>
          </div>
          <button
            onClick={handleAdd}
            disabled={!produto.disponivel || adding}
            className="btn-outline"
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              opacity: !produto.disponivel ? 0.4 : 1,
            }}
          >
            {added ? tp('adicionado') : tp('adicionar')}
          </button>
        </div>
      </div>
    </a>
  );
}

/** Lista horizontal: melhor para comparar specs e preço rapidamente. */
function ProdutoLinha({ produto }: { produto: Produto }) {
  const tp = useTranslations('produtos');
  const { adding, added, handleAdd } = useAdicionar(produto);

  return (
    <a
      href={`/produto/${produto.slug}`}
      className="flex items-center gap-4 py-4"
      style={{ borderBottom: '1px solid #f0f0f0' }}
    >
      <div
        className="relative flex-shrink-0 overflow-hidden bg-gray-100"
        style={{ width: '80px', height: '80px', borderRadius: '4px' }}
      >
        {produto.imagem ? (
          <img src={produto.imagem} alt={produto.nome} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
            {tp('semImagem')}
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {produto.categoria && (
            <p
              className="text-gray-500"
              style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em' }}
            >
              {produto.categoria}
            </p>
          )}
          {produto.destaque && (
            <span
              className="text-white text-xs px-2 py-0.5 font-medium"
              style={{ background: '#000', borderRadius: '2px', fontSize: '10px' }}
            >
              {tp('destaque')}
            </span>
          )}
          {!produto.disponivel && (
            <span className="text-red-600" style={{ fontSize: '11px' }}>
              {tp('indisponivel')}
            </span>
          )}
        </div>
        <p className="text-sm font-medium truncate">{produto.nome}</p>
        {produto.descricaoCurta && (
          <p className="text-gray-500 text-xs truncate" style={{ maxWidth: '480px' }}>
            {produto.descricaoCurta}
          </p>
        )}
      </div>

      <div className="text-right flex-shrink-0">
        {produto.precoDe && (
          <p className="text-gray-400 text-xs" style={{ textDecoration: 'line-through' }}>
            {money(produto.precoDe)}
          </p>
        )}
        <p className="font-semibold text-sm">{moneyMensal(produto.preco)}</p>
      </div>

      <button
        onClick={handleAdd}
        disabled={!produto.disponivel || adding}
        className="btn-outline flex-shrink-0"
        style={{ padding: '6px 14px', fontSize: '12px', opacity: !produto.disponivel ? 0.4 : 1 }}
      >
        {added ? tp('adicionado') : tp('adicionar')}
      </button>
    </a>
  );
}

function LayoutToggle({ layout, onChange }: { layout: Layout; onChange: (l: Layout) => void }) {
  const t = useTranslations('paginaCatalogo');
  const options: { value: Layout; label: string }[] = [
    { value: 'post', label: t('layoutPost') },
    { value: 'grid3', label: t('layoutGrid') },
    { value: 'lista', label: t('layoutLista') },
  ];

  return (
    <div className="flex gap-1 flex-shrink-0" role="group" aria-label={t('layoutAria')}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className="text-xs font-medium transition-colors duration-150"
          style={{
            border: '1px solid #000',
            padding: '7px 12px',
            background: layout === o.value ? '#000' : 'transparent',
            color: layout === o.value ? '#fff' : '#000',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function CatalogoPage() {
  return (
    <Suspense fallback={null}>
      <CatalogoPageInner />
    </Suspense>
  );
}

function CatalogoPageInner() {
  const t = useTranslations('paginaCatalogo');
  const searchParams = useSearchParams();
  const locale = useLocale();
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [categoriaAtiva, setCategoriaAtiva] = useState(() => searchParams.get('categoria') ?? '');
  const [busca, setBusca] = useState('');
  const [buscaInput, setBuscaInput] = useState('');
  const [ordem, setOrdem] = useState<'' | 'preco_asc' | 'preco_desc' | 'nome'>('');
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [layout, setLayout] = useState<Layout>('post');

  useEffect(() => {
    const saved = window.localStorage.getItem(LAYOUT_KEY);
    if (saved === 'post' || saved === 'grid3' || saved === 'lista') {
      setLayout(saved);
    }
  }, []);

  function handleLayoutChange(l: Layout) {
    setLayout(l);
    window.localStorage.setItem(LAYOUT_KEY, l);
  }

  useEffect(() => {
    listarCategorias(locale)
      .then(({ categorias }) => setCategorias(categorias))
      .catch(() => {});
  }, [locale]);

  useEffect(() => {
    setLoading(true);
    setErro('');
    listarProdutos({
      categoria: categoriaAtiva || undefined,
      busca: busca || undefined,
      ordem: ordem || undefined,
      limite: 60,
      locale,
    })
      .then(({ produtos }) => setProdutos(produtos))
      .catch(() => setErro(t('erro')))
      .finally(() => setLoading(false));
  }, [categoriaAtiva, busca, ordem, locale, t]);

  return (
    <>
      <SiteHeader />
      <main className="bg-white" style={{ minHeight: '70vh' }}>
        <div className="container-site" style={{ padding: '48px 0 96px' }}>
          <p className="eyebrow mb-3" style={{ color: '#1a9fff' }}>
            {t('eyebrow')}
          </p>
          <h1 className="display-section mb-10">{t('titulo')}</h1>

          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setBusca(buscaInput.trim());
              }}
              className="flex gap-2 flex-1"
            >
              <input
                type="text"
                value={buscaInput}
                onChange={(e) => setBuscaInput(e.target.value)}
                placeholder={t('placeholderBusca')}
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
                style={{ maxWidth: '360px' }}
              />
              <button
                type="submit"
                className="btn-outline"
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                {t('botaoBuscar')}
              </button>
            </form>

            <select
              value={ordem}
              onChange={(e) => setOrdem(e.target.value as typeof ordem)}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">{t('ordenarDestaque')}</option>
              <option value="preco_asc">{t('ordenarPrecoAsc')}</option>
              <option value="preco_desc">{t('ordenarPrecoDesc')}</option>
              <option value="nome">{t('ordenarNome')}</option>
            </select>

            <LayoutToggle layout={layout} onChange={handleLayoutChange} />
          </div>

          {categorias.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10">
              <button
                onClick={() => setCategoriaAtiva('')}
                className="nav-link"
                style={{
                  border: '1px solid #000',
                  borderRadius: '999px',
                  padding: '4px 14px',
                  background: categoriaAtiva === '' ? '#000' : 'transparent',
                  color: categoriaAtiva === '' ? '#fff' : '#000',
                }}
              >
                {t('todasCategorias')}
              </button>
              {categorias.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategoriaAtiva(c.slug)}
                  className="nav-link"
                  style={{
                    border: '1px solid #000',
                    borderRadius: '999px',
                    padding: '4px 14px',
                    background: categoriaAtiva === c.slug ? '#000' : 'transparent',
                    color: categoriaAtiva === c.slug ? '#fff' : '#000',
                  }}
                >
                  {c.nome} ({c.produtos})
                </button>
              ))}
            </div>
          )}

          {loading && <p className="text-gray-500 text-sm">{t('carregando')}</p>}
          {!loading && erro && <p className="text-red-600 text-sm">{erro}</p>}
          {!loading && !erro && produtos.length === 0 && (
            <p className="text-gray-500 text-sm">{t('vazio')}</p>
          )}

          {!loading && !erro && produtos.length > 0 && layout === 'post' && (
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-6 mx-auto"
              style={{ maxWidth: '860px' }}
            >
              {produtos.map((p) => (
                <ProdutoCardPost key={p.id} produto={p} />
              ))}
            </div>
          )}

          {!loading && !erro && produtos.length > 0 && layout === 'grid3' && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {produtos.map((p) => (
                <ProdutoCardGrid key={p.id} produto={p} />
              ))}
            </div>
          )}

          {!loading && !erro && produtos.length > 0 && layout === 'lista' && (
            <div>
              {produtos.map((p) => (
                <ProdutoLinha key={p.id} produto={p} />
              ))}
            </div>
          )}
        </div>
      </main>
      <StoreFooter />
    </>
  );
}
